import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import { 
  Activity, AlertTriangle, TrendingUp, Users, 
  CheckCircle, Clock, CalendarDays, Filter, Building2, Briefcase, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#0d9488', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#06b6d4', '#84cc16', '#a855f7', '#22c55e', '#e11d48', '#0ea5e9', '#d946ef'];

const fmtDD = (isoDate) => {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  if (parts.length === 2) return `${parts[1]}-${parts[0]}`;
  return isoDate;
};

const toISO = (d) => {
  // Format as YYYY-MM-DD in Chile timezone
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
};

const nowChile = () => {
  const s = new Date().toLocaleString('en-US', { timeZone: 'America/Santiago' });
  return new Date(s);
};

const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase()
    .replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase())
    .replace(/\bs\.a\.\b/gi, 'S.A.')
    .replace(/\bs\.a\b/gi, 'S.A.')
    .replace(/\bspa\.?\b/gi, 'SPA')
    .replace(/\bltda\.?\b/gi, 'LTDA')
    .replace(/\beirl\.?\b/gi, 'EIRL')
    .replace(/\bsrl\.?\b/gi, 'SRL')
    .replace(/\b(De|Del|La|Las|Los|El|En|Y|E|Con|Por|Para|Al)\b/g, m => m.toLowerCase())
    .replace(/^./, c => c.toUpperCase());
};

const KpiCard = ({ icon: Icon, label, value, color, sub, onClick }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-5 shadow-sm ${onClick ? 'cursor-pointer hover:border-slate-300 hover:shadow-md transition-all' : ''}`}
    onClick={onClick} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value?.toLocaleString()}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
    {onClick && <p className="text-[10px] text-slate-400 mt-2 text-right">Click para ver detalle</p>}
  </div>
);

const PRESETS = [
  { label: 'Hoy', getValue: () => { const t = toISO(nowChile()); return [t, t]; }},
  { label: 'Última semana', getValue: () => { const h = nowChile(); const d = new Date(h); d.setDate(d.getDate()-7); return [toISO(d), toISO(h)]; }},
  { label: 'Último mes', getValue: () => { const h = nowChile(); const d = new Date(h); d.setMonth(d.getMonth()-1); return [toISO(d), toISO(h)]; }},
  { label: 'Últimos 3 meses', getValue: () => { const h = nowChile(); const d = new Date(h); d.setMonth(d.getMonth()-3); return [toISO(d), toISO(h)]; }},
  { label: 'Todo', getValue: () => ['', ''] },
];

export const InformeGCOM = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [gerenciaId, setGerenciaId] = useState('');
  const [empresaId, setEmpresaId] = useState('');
  const [activePreset, setActivePreset] = useState('Todo');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalTipo, setModalTipo] = useState('');

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (gerenciaId) params.gerencia_id = gerenciaId;
      if (empresaId) params.empresa_id = empresaId;
      const res = await axios.get(`${API}/gcom/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar dashboard GCOM');
    } finally {
      setLoading(false);
    }
  }, [token, fechaDesde, fechaHasta, gerenciaId, empresaId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const openDetail = async (tipo) => {
    setModalTipo(tipo);
    setModalOpen(true);
    setModalLoading(true);
    try {
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (gerenciaId) params.gerencia_id = gerenciaId;
      if (empresaId) params.empresa_id = empresaId;
      const res = await axios.get(`${API}/gcom/detalle/${tipo}`, { headers: { Authorization: `Bearer ${token}` }, params });
      setModalData(res.data);
    } catch (e) {
      toast.error('Error al cargar detalle');
      setModalOpen(false);
    } finally {
      setModalLoading(false);
    }
  };

  const applyPreset = (preset) => {
    const [desde, hasta] = preset.getValue();
    setFechaDesde(desde);
    setFechaHasta(hasta);
    setActivePreset(preset.label);
  };

  const handleCustomDate = (field, value) => {
    if (field === 'desde') setFechaDesde(value);
    else setFechaHasta(value);
    setActivePreset('');
  };

  if (!data && loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
    </div>
  );
  if (!data) return <div className="text-center py-20 text-slate-500">Error al cargar datos</div>;

  const { kpis, tendencia_mensual, por_tipo, top_peligros, factores_humanos, tendencia_diaria, por_gerencia: rawGerencia, acciones_por_gerencia: rawAccGer, incidentes_gerencia: rawIncGer, gerencias: rawGerencias, empresas: rawEmpresas, por_empresa: rawEmpresa, acciones_por_empresa: rawAccEmp, incidentes_empresa: rawIncEmp } = data;

  // Title Case para gerencias y empresas
  const gerencias = (rawGerencias || []).map(g => ({ ...g, nombre: toTitleCase(g.nombre) }));
  const empresas = (rawEmpresas || []).map(e => ({ ...e, nombre: toTitleCase(e.nombre) }));
  const por_gerencia = (rawGerencia || []).map(g => ({ ...g, gerencia: toTitleCase(g.gerencia) }));
  const acciones_por_gerencia = (rawAccGer || []).map(g => ({ ...g, gerencia: toTitleCase(g.gerencia) }));
  const incidentes_gerencia = (rawIncGer || []).map(g => ({ ...g, gerencia: toTitleCase(g.gerencia) }));
  const por_empresa = (rawEmpresa || []).map(e => ({ ...e, empresa: toTitleCase(e.empresa) }));
  const acciones_por_empresa = (rawAccEmp || []).map(e => ({ ...e, empresa: toTitleCase(e.empresa) }));
  const incidentes_empresa = (rawIncEmp || []).map(e => ({ ...e, empresa: toTitleCase(e.empresa) }));
  const totalAcciones = kpis.acciones_pendientes + kpis.acciones_cerradas;
  const pctCerradas = totalAcciones > 0 ? ((kpis.acciones_cerradas / totalAcciones) * 100).toFixed(1) : 0;
  const selectedGerencia = gerenciaId ? (gerencias || []).find(g => g.id === Number(gerenciaId))?.nombre : null;
  const selectedEmpresa = empresaId ? (empresas || []).find(e => e.id === Number(empresaId))?.nombre : null;

  return (
    <div className="space-y-6" data-testid="informe-gcom-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Informe GCOM</h1>
          {selectedGerencia && <p className="text-sm text-teal-600 font-medium mt-1">{selectedGerencia}</p>}
          {selectedEmpresa && <p className="text-sm text-indigo-600 font-medium mt-0.5">{selectedEmpresa}</p>}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="date-filter">
        <div className="flex flex-col gap-3">
          {/* Row 1: Período + Dates */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700 shrink-0">
              <Filter className="w-4 h-4" />
              <span>Período:</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button key={p.label} onClick={() => applyPreset(p)}
                  className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
                    activePreset === p.label ? 'bg-teal-600 text-white border-teal-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`} data-testid={`preset-${p.label.toLowerCase().replace(/\s/g, '-')}`}
                >{p.label}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 lg:ml-auto shrink-0">
              <CalendarDays className="w-4 h-4 text-slate-400" />
              <input type="date" value={fechaDesde} onChange={(e) => handleCustomDate('desde', e.target.value)}
                className="px-2 py-1 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 w-36" data-testid="date-from" />
              <span className="text-slate-400 text-xs">a</span>
              <input type="date" value={fechaHasta} onChange={(e) => handleCustomDate('hasta', e.target.value)}
                className="px-2 py-1 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 w-36" data-testid="date-to" />
            </div>
          </div>
          {/* Row 2: Gerencia + Empresa ESE */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Building2 className="w-4 h-4 text-slate-500 shrink-0" />
              <span className="text-sm font-medium text-slate-700 shrink-0">Gerencia:</span>
              <select value={gerenciaId} onChange={(e) => setGerenciaId(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                data-testid="gerencia-filter">
                <option value="">Todas las gerencias</option>
                {(gerencias || []).map(g => (
                  <option key={g.id} value={g.id}>{g.nombre}</option>
                ))}
              </select>
              {gerenciaId && <button onClick={() => setGerenciaId('')} className="text-xs text-teal-600 hover:underline shrink-0">Limpiar</button>}
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Briefcase className="w-4 h-4 text-slate-500 shrink-0" />
              <span className="text-sm font-medium text-slate-700 shrink-0">Empresa ESE:</span>
              <select value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                data-testid="empresa-filter">
                <option value="">Todas las empresas</option>
                {(empresas || []).map(e => (
                  <option key={e.id} value={e.id}>{e.nombre}</option>
                ))}
              </select>
              {empresaId && <button onClick={() => setEmpresaId('')} className="text-xs text-indigo-600 hover:underline shrink-0">Limpiar</button>}
            </div>
          </div>
        </div>
        {(fechaDesde || fechaHasta) && (
          <p className="text-xs text-teal-600 mt-2">
            Mostrando datos {fechaDesde ? `desde ${fmtDD(fechaDesde)}` : ''} {fechaHasta ? `hasta ${fmtDD(fechaHasta)}` : ''}
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={Activity} label="Total GCOMs" value={kpis.total_gcom} color="bg-teal-600" />
            <KpiCard icon={AlertTriangle} label="Con Accidentes" value={kpis.con_accidentes} color="bg-red-500" sub={`${kpis.con_hallazgos} hallazgos`} onClick={() => openDetail('accidentes')} />
            <KpiCard icon={TrendingUp} label="Con Mejoras" value={kpis.con_mejoras} color="bg-amber-500" onClick={() => openDetail('mejoras')} />
            <KpiCard icon={Users} label="Participantes" value={kpis.participantes_unicos} color="bg-indigo-500" sub={`${kpis.total_asistencias.toLocaleString()} asistencias`} />
          </div>

          {/* Estado + Acciones + Tipo */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Estado de GCOMs</h3>
              <div className="space-y-2">
                {Object.entries(kpis.estados).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${k === 'ABIERTO' ? 'bg-amber-400' : k === 'FINALIZADO' ? 'bg-green-400' : 'bg-blue-400'}`} />
                      <span className="text-sm text-slate-600 capitalize">{k.toLowerCase()}</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900">{v.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Acciones Correctivas</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" /><span className="text-sm text-slate-600">Pendientes</span></div>
                  <span className="text-sm font-bold text-amber-600">{kpis.acciones_pendientes.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm text-slate-600">Cerradas</span></div>
                  <span className="text-sm font-bold text-green-600">{kpis.acciones_cerradas}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 mt-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: `${pctCerradas}%` }} />
                </div>
                <p className="text-xs text-slate-400 text-center">{pctCerradas}% completadas</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">GCOMs por Tipo</h3>
              <ResponsiveContainer width="100%" height={120}>
                <PieChart>
                  <Pie data={por_tipo} dataKey="total" nameKey="tipo" cx="50%" cy="50%" outerRadius={50} label={({ tipo, percent }) => `${tipo} (${(percent * 100).toFixed(0)}%)`}>
                    {por_tipo.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* GCOMs por Gerencia */}
          {por_gerencia?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">GCOMs por Gerencia</h3>
              <ResponsiveContainer width="100%" height={Math.max(300, por_gerencia.length * 28)}>
                <BarChart data={por_gerencia} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="gerencia" width={220} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#0d9488" radius={[0, 4, 4, 0]} name="GCOMs" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Incidentes por Gerencia */}
          {incidentes_gerencia?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Incidentes por Gerencia</h3>
              <ResponsiveContainer width="100%" height={Math.max(300, incidentes_gerencia.length * 28)}>
                <BarChart data={incidentes_gerencia} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="gerencia" width={220} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="accidentes" fill="#ef4444" name="Accidentes" stackId="a" />
                  <Bar dataKey="hallazgos" fill="#f59e0b" name="Hallazgos" stackId="a" />
                  <Bar dataKey="mejoras" fill="#22c55e" name="Mejoras" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Acciones Pendientes por Gerencia */}
          {acciones_por_gerencia?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Acciones Correctivas Pendientes por Gerencia</h3>
              <ResponsiveContainer width="100%" height={Math.max(280, acciones_por_gerencia.length * 28)}>
                <BarChart data={acciones_por_gerencia} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="gerencia" width={220} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Pendientes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* === EMPRESA CONTRATISTA (ESE) SECTION === */}
          {por_empresa?.length > 0 && (
            <>
              <div className="border-t border-slate-200 pt-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-indigo-500" />
                  Empresas Contratistas (ESE)
                </h2>
              </div>

              {/* GCOMs por Empresa */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">GCOMs por Empresa Contratista</h3>
                <ResponsiveContainer width="100%" height={Math.max(400, por_empresa.length * 28)}>
                  <BarChart data={por_empresa} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="empresa" width={280} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="total" fill="#6366f1" radius={[0, 4, 4, 0]} name="GCOMs" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Incidentes por Empresa */}
              {incidentes_empresa?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4">Incidentes por Empresa Contratista</h3>
                  <ResponsiveContainer width="100%" height={Math.max(400, incidentes_empresa.length * 28)}>
                    <BarChart data={incidentes_empresa} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="empresa" width={280} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="accidentes" fill="#ef4444" name="Accidentes" stackId="a" />
                      <Bar dataKey="hallazgos" fill="#f59e0b" name="Hallazgos" stackId="a" />
                      <Bar dataKey="mejoras" fill="#22c55e" name="Mejoras" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Acciones Pendientes por Empresa */}
              {acciones_por_empresa?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4">Acciones Correctivas Pendientes por Empresa Contratista</h3>
                  <ResponsiveContainer width="100%" height={Math.max(400, acciones_por_empresa.length * 28)}>
                    <BarChart data={acciones_por_empresa} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="empresa" width={280} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="total" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Pendientes" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}

          {/* Tendencia mensual */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Tendencia Mensual de GCOMs</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={tendencia_mensual}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} tickFormatter={(v) => { const p = v.split('-'); return `${p[1]}-${p[0]}`; }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip labelFormatter={fmtDD} />
                <Bar dataKey="total" fill="#0d9488" radius={[4, 4, 0, 0]} name="GCOMs" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tendencia diaria */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">
              {fechaDesde && fechaHasta ? `GCOMs del ${fmtDD(fechaDesde)} al ${fmtDD(fechaHasta)}` : 'GCOMs Últimos 30 Días'}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={tendencia_diaria}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="dia" tick={{ fontSize: 10 }} tickFormatter={(v) => { const p = v.split('-'); return `${p[2]}-${p[1]}`; }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip labelFormatter={fmtDD} />
                <Line type="monotone" dataKey="total" stroke="#0d9488" strokeWidth={2} dot={false} name="GCOMs" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Top Peligros + Factores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Top 10 Peligros Identificados</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={top_peligros} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="peligro" width={180} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#ef4444" radius={[0, 4, 4, 0]} name="Frecuencia" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Factores Humanos Reportados</h3>
              {factores_humanos?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={factores_humanos} dataKey="total" nameKey="factor" cx="50%" cy="50%" outerRadius={100} label={({ factor, percent }) => `${factor} (${(percent * 100).toFixed(0)}%)`}>
                      {factores_humanos.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400 text-center py-10">Sin datos de factores humanos</p>}
            </div>
          </div>

        </>
      )}

      {/* Modal Detalle */}
      {modalOpen && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="detail-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">
                {modalTipo === 'accidentes' && 'GCOMs con Accidentes'}
                {modalTipo === 'hallazgos' && 'GCOMs con Hallazgos'}
                {modalTipo === 'mejoras' && 'GCOMs con Mejoras'}
                {modalData && <span className="text-sm font-normal text-slate-500 ml-2">({modalData.total} registros)</span>}
              </h2>
              <button onClick={() => setModalOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="close-modal">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-auto flex-1 px-6 py-4">
              {modalLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
                </div>
              ) : modalData?.registros?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">ID</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Fecha</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Tipo</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Gerencia</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Empresa</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Indicadores</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.registros.map((r, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-3 py-2 text-slate-500 font-mono text-xs">{r.id_gcom}</td>
                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.fecha}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.tipo === 'ESE' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'}`}>
                            {r.tipo}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{toTitleCase(r.gerencia)}</td>
                        <td className="px-3 py-2 text-slate-700">{toTitleCase(r.empresa)}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1 flex-wrap">
                            {r.accidentes === 'SI' && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">Accidente</span>}
                            {r.hallazgos === 'SI' && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">Hallazgo</span>}
                            {r.mejoras === 'SI' && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">Mejora</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center text-slate-500 py-8">No se encontraron registros</p>
              )}
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default InformeGCOM;
